import type { Field } from '@puckeditor/core';

import { NumericField } from './widgets/number-field';
import { lookup } from './tokens';

export const TEXT_WEIGHTS = {
  normal: 'var(--dcm-cms-text-weight-normal, 400)',
  medium: 'var(--dcm-cms-text-weight-medium, 500)',
  semibold: 'var(--dcm-cms-text-weight-semibold, 600)',
  bold: 'var(--dcm-cms-text-weight-bold, 700)',
} as const;

export type TextWeight = keyof typeof TEXT_WEIGHTS | 'system';

export const TEXT_WEIGHT_FIELD: Field<TextWeight> = {
  type: 'select',
  label: 'Font weight',
  options: [
    { label: 'Default', value: 'system' },
    { label: 'Normal', value: 'normal' },
    { label: 'Medium', value: 'medium' },
    { label: 'Semibold', value: 'semibold' },
    { label: 'Bold', value: 'bold' },
  ],
};

/** Custom pixel size bounds: one source for the input, the AI schema and the CSS clamp. */
export const CUSTOM_TEXT_SIZE_MIN = 8;
export const CUSTOM_TEXT_SIZE_MAX = 200;
export const CUSTOM_TEXT_SIZE_STEP = 1;

/**
 * The Custom size control. `defaultSize` is the block's renderer fallback (Heading 32, Paragraph 16),
 * so the input shows what the page renders when the saved size is absent or malformed.
 */
export function customTextSizeField(defaultSize: number): Field<number> {
  return {
    type: 'custom',
    label: 'Custom size',
    render: NumericField('px', {
      min: CUSTOM_TEXT_SIZE_MIN,
      max: CUSTOM_TEXT_SIZE_MAX,
      step: CUSTOM_TEXT_SIZE_STEP,
      fallback: defaultSize,
      // The 'Custom text size' switch directly above already names this control.
      hideLabel: true,
    }),
    ai: {
      schema: { type: 'number', minimum: CUSTOM_TEXT_SIZE_MIN, maximum: CUSTOM_TEXT_SIZE_MAX },
      instructions: `Pixel size, ${CUSTOM_TEXT_SIZE_MIN} to ${CUSTOM_TEXT_SIZE_MAX}. Only used when the size switch is Custom.`,
    },
  };
}

export function customTextSizeCss(value: unknown, defaultSize: number): string {
  const pixels = typeof value === 'number' && Number.isFinite(value) ? value : defaultSize;
  return `${Math.min(CUSTOM_TEXT_SIZE_MAX, Math.max(CUSTOM_TEXT_SIZE_MIN, Math.round(pixels)))}px`;
}

export function textWeightCss(weight: TextWeight): string | undefined {
  return lookup(TEXT_WEIGHTS, weight, undefined);
}
